import { createSelector } from "reselect"
import { get, map } from "lodash"
import { getCurrent, getRawFactorSensitivityData } from "./sensitivity"
import { getFactorCategory, getFactorLabel, makeNonZeroKeysSelectorFromData, computeAvailableSelectViewFactors } from "helpers/factors"
import { getCategories, getSelectedViews, getSelectViewAggregatedFactorsMetadata, getFactorsMetadata } from "selectors/factors"
import { getFavoriteFactors, getMustAggregateFactors, getStyleExposureAggregation } from "modules/portfolio-root/selectors/portfolios"
import { computeDisplayType, computeDisplayTypeName, getAllCategoryItems as getAllCategoryItemsFn, getOrderedCategoryItems as getOrderedCategoryItemsFn } from "helpers/analyze"
import { getModelFactorsClassification } from "selectors/modelMetadata"

const getDisabled = state => get(state, "modules.analyzeFactorSensitivity.disabled")
const getRawDisplayType = state => get(state, "modules.analyzeFactorSensitivity.displayType")

export const getDisplayType = createSelector(
  [getRawDisplayType, getCategories],
  computeDisplayType,
)

export const getDisplayTypeName = createSelector(
  [getRawDisplayType, getCategories],
  computeDisplayTypeName,
)

export const getDisplayAggregationToggle = createSelector(
  [getDisplayType, getMustAggregateFactors],
  (displayType, mustAggregateFactors) => mustAggregateFactors && displayType === "style",
)

export const getAllItems = createSelector(
  [getFactorsMetadata, getCurrent, getDisabled, getFavoriteFactors],
  (factorsMetadata, lastDatum, disabled, favoriteFactors) => map(factorsMetadata, (_, id) => ({
    id,
    label: getFactorLabel(factorsMetadata, id),
    category: getFactorCategory(factorsMetadata, id),
    favorite: favoriteFactors[id],
    lastValue: get(lastDatum, ["factors", id], 0) * 100,
    field: `factors.${id}`,
    disabled: !!disabled[id],
    isAggregated: false,
    selectedView: factorsMetadata[id].selectedView,
  })),
)

const getNonZerosKeys = makeNonZeroKeysSelectorFromData({
  getter: value => get(value, "factors"),
  isAboveZeroTreshold: (value) => Math.abs(value) !== 0,
  dataSelector: getRawFactorSensitivityData,
  favoriteSelector: getFavoriteFactors,
  factorValueField: "sensitivity",
})

export const getAvailableSelectViewFactors = createSelector(
  [getSelectViewAggregatedFactorsMetadata, getModelFactorsClassification, getSelectedViews, getNonZerosKeys],
  computeAvailableSelectViewFactors,
)

export const getAllCategoryItems = createSelector(
  [getAllItems, getDisplayType, getStyleExposureAggregation],
  getAllCategoryItemsFn,
)

export const getOrderedCategoryItems = createSelector(
  [getAllItems, getDisplayType, getStyleExposureAggregation],
  getOrderedCategoryItemsFn,
)
